import { saveSetting, SETTINGS_KEYS } from '@/lib/settingsSync';

export interface RolePermissions {
  screens: Record<string, boolean>;
  actions: Record<string, boolean>;
}

export type PermissionsMap = Record<string, RolePermissions>;

export interface PermissionItem {
  key: string;
  labelAr: string;
  labelEn: string;
}

const STORAGE_KEY = 'app_role_permissions';
const SCREENS_KEY = 'app_permission_screens';
const ACTIONS_KEY = 'app_permission_actions';

const DEFAULT_SCREENS: PermissionItem[] = [
  { key: 'dashboard', labelAr: 'لوحة التحكم', labelEn: 'Dashboard' },
  { key: 'entry', labelAr: 'الإدخال', labelEn: 'Entry' },
  { key: 'shipments', labelAr: 'الشحنات', labelEn: 'Shipments' },
  { key: 'followUp', labelAr: 'المتابعة', labelEn: 'Follow Up' },
  { key: 'accounting', labelAr: 'المحاسبة', labelEn: 'Accounting' },
  { key: 'driverApp', labelAr: 'تطبيق السائق', labelEn: 'Driver App' },
  { key: 'customerApp', labelAr: 'تطبيق العميل', labelEn: 'Customer App' },
  { key: 'customers', labelAr: 'العملاء', labelEn: 'Customers' },
  { key: 'drivers', labelAr: 'السائقين', labelEn: 'Drivers' },
  { key: 'inventory', labelAr: 'المخزون', labelEn: 'Inventory' },
  { key: 'settings', labelAr: 'الإعدادات', labelEn: 'Settings' },
];

const DEFAULT_ACTIONS: PermissionItem[] = [
  { key: 'createShipment', labelAr: 'إنشاء شحنة', labelEn: 'Create Shipment' },
  { key: 'editShipment', labelAr: 'تعديل شحنة', labelEn: 'Edit Shipment' },
  { key: 'deleteShipment', labelAr: 'حذف شحنة', labelEn: 'Delete Shipment' },
  { key: 'changeStatus', labelAr: 'تغيير الحالة', labelEn: 'Change Status' },
  { key: 'exportData', labelAr: 'تصدير البيانات', labelEn: 'Export Data' },
  { key: 'deleteSettlement', labelAr: 'حذف تسوية', labelEn: 'Delete Settlement' },
];

// ---- Screens CRUD ----
export const getScreens = (): PermissionItem[] => {
  try {
    const raw = localStorage.getItem(SCREENS_KEY);
    if (raw) {
      const stored: PermissionItem[] = JSON.parse(raw);
      const storedKeys = new Set(stored.map(s => s.key));
      const missing = DEFAULT_SCREENS.filter(s => !storedKeys.has(s.key));
      if (missing.length > 0) {
        const merged = [...stored, ...missing];
        saveScreens(merged);
        return merged;
      }
      return stored;
    }
  } catch {}
  return DEFAULT_SCREENS;
};

export const saveScreens = (screens: PermissionItem[]) => {
  localStorage.setItem(SCREENS_KEY, JSON.stringify(screens));
  saveSetting(SETTINGS_KEYS.SCREENS, screens);
};

export const addScreen = (item: PermissionItem) => {
  const screens = getScreens();
  screens.push(item);
  saveScreens(screens);
};

export const updateScreen = (oldKey: string, item: PermissionItem) => {
  const screens = getScreens();
  const idx = screens.findIndex(s => s.key === oldKey);
  if (idx !== -1) screens[idx] = item;
  saveScreens(screens);
};

export const deleteScreen = (key: string) => {
  const screens = getScreens().filter(s => s.key !== key);
  saveScreens(screens);
};

// ---- Actions CRUD ----
export const getActions = (): PermissionItem[] => {
  try {
    const raw = localStorage.getItem(ACTIONS_KEY);
    if (raw) {
      const stored: PermissionItem[] = JSON.parse(raw);
      const storedKeys = new Set(stored.map(a => a.key));
      const missing = DEFAULT_ACTIONS.filter(a => !storedKeys.has(a.key));
      if (missing.length > 0) {
        const merged = [...stored, ...missing];
        saveActions(merged);
        return merged;
      }
      return stored;
    }
  } catch {}
  return DEFAULT_ACTIONS;
};

export const saveActions = (actions: PermissionItem[]) => {
  localStorage.setItem(ACTIONS_KEY, JSON.stringify(actions));
  saveSetting(SETTINGS_KEYS.ACTIONS, actions);
};

export const addAction = (item: PermissionItem) => {
  const actions = getActions();
  actions.push(item);
  saveActions(actions);
};

export const updateAction = (oldKey: string, item: PermissionItem) => {
  const actions = getActions();
  const idx = actions.findIndex(a => a.key === oldKey);
  if (idx !== -1) actions[idx] = item;
  saveActions(actions);
};

export const deleteAction = (key: string) => {
  const actions = getActions().filter(a => a.key !== key);
  saveActions(actions);
};

// Keep SCREENS/ACTIONS as aliases for backward compat
export const SCREENS = DEFAULT_SCREENS;
export const ACTIONS = DEFAULT_ACTIONS;

const ROLES_KEY = 'app_permission_roles';

const DEFAULT_ROLES: PermissionItem[] = [
  { key: 'admin', labelAr: 'مدير', labelEn: 'Admin' },
  { key: 'employee', labelAr: 'موظف', labelEn: 'Employee' },
  { key: 'driver', labelAr: 'سائق', labelEn: 'Driver' },
  { key: 'customer', labelAr: 'عميل', labelEn: 'Customer' },
  { key: 'customer_follow-up', labelAr: 'متابعة عملاء', labelEn: 'Customer Follow-up' },
];

export const getRoles = (): PermissionItem[] => {
  try {
    const raw = localStorage.getItem(ROLES_KEY);
    if (raw) {
      const stored: PermissionItem[] = JSON.parse(raw);
      const storedKeys = new Set(stored.map(r => r.key));
      const missing = DEFAULT_ROLES.filter(r => !storedKeys.has(r.key));
      if (missing.length > 0) {
        const merged = [...stored, ...missing];
        saveRoles(merged);
        return merged;
      }
      return stored;
    }
  } catch {}
  return DEFAULT_ROLES;
};

export const saveRoles = (roles: PermissionItem[]) => {
  localStorage.setItem(ROLES_KEY, JSON.stringify(roles));
  saveSetting(SETTINGS_KEYS.ROLES, roles);
};

export const addRole = (item: PermissionItem) => {
  const roles = getRoles();
  roles.push(item);
  saveRoles(roles);
};

export const updateRole = (oldKey: string, item: PermissionItem) => {
  const roles = getRoles();
  const idx = roles.findIndex(r => r.key === oldKey);
  if (idx !== -1) roles[idx] = item;
  saveRoles(roles);
};

export const deleteRole = (key: string) => {
  if (key === 'admin') return;
  const roles = getRoles().filter(r => r.key !== key);
  saveRoles(roles);
  const perms = getPermissions();
  delete perms[key];
  savePermissions(perms);
};

// Keep MANAGED_ROLES as alias for backward compat
export const MANAGED_ROLES = DEFAULT_ROLES;

const getDefaultPermissions = (): PermissionsMap => {
  const screens = getScreens();
  const actions = getActions();
  const roles = getRoles();
  const allScreens = Object.fromEntries(screens.map(s => [s.key, true]));
  const allActions = Object.fromEntries(actions.map(a => [a.key, true]));

  const perms: PermissionsMap = {};
  roles.forEach(role => {
    if (role.key === 'admin') {
      perms.admin = { screens: { ...allScreens }, actions: { ...allActions } };
    } else if (role.key === 'employee') {
      perms.employee = {
        screens: { ...allScreens, settings: false },
        actions: { ...allActions, deleteShipment: false, manageDrivers: false, deleteSettlement: false },
      };
    } else if (role.key === 'driver') {
      perms.driver = {
        screens: { ...Object.fromEntries(screens.map(s => [s.key, false])), driverApp: true },
        actions: Object.fromEntries(actions.map(a => [a.key, false])),
      };
    } else if (role.key === 'customer') {
      perms.customer = {
        screens: { ...Object.fromEntries(screens.map(s => [s.key, false])), customerApp: true },
        actions: Object.fromEntries(actions.map(a => [a.key, false])),
      };
    } else {
      perms[role.key] = {
        screens: Object.fromEntries(screens.map(s => [s.key, false])),
        actions: Object.fromEntries(actions.map(a => [a.key, false])),
      };
    }
  });
  return perms;
};

export const getPermissions = (): PermissionsMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const perms: PermissionsMap = JSON.parse(raw);
      
      const screens = getScreens();
      const actions = getActions();
      const allScreens = Object.fromEntries(screens.map(s => [s.key, true]));
      const allActions = Object.fromEntries(actions.map(a => [a.key, true]));
      perms.admin = { screens: { ...allScreens }, actions: { ...allActions } };
      
      const roles = getRoles();
      let changed = false;
      roles.forEach(role => {
        if (role.key === 'admin') return;
        if (!perms[role.key]) {
          perms[role.key] = {
            screens: Object.fromEntries(screens.map(s => [s.key, false])),
            actions: Object.fromEntries(actions.map(a => [a.key, false])),
          };
          changed = true;
        } else {
          screens.forEach(s => {
            if (perms[role.key].screens[s.key] === undefined) {
              perms[role.key].screens[s.key] = false;
              changed = true;
            }
          });
          actions.forEach(a => {
            if (perms[role.key].actions[a.key] === undefined) {
              perms[role.key].actions[a.key] = false;
              changed = true;
            }
          });
        }
      });
      
      savePermissions(perms);
      return perms;
    }
  } catch {}
  return getDefaultPermissions();
};

export const savePermissions = (perms: PermissionsMap) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(perms));
  saveSetting(SETTINGS_KEYS.PERMISSIONS, perms);
};

export const resetPermissions = (): PermissionsMap => {
  const defaults = getDefaultPermissions();
  savePermissions(defaults);
  return defaults;
};

const SAVED_DEFAULTS_KEY = 'app_saved_default_permissions';

export const saveAsDefaults = () => {
  const currentPerms = getPermissions();
  const currentRoles = getRoles();
  const currentScreens = getScreens();
  const currentActions = getActions();
  localStorage.setItem(SAVED_DEFAULTS_KEY, JSON.stringify({
    permissions: currentPerms,
    roles: currentRoles,
    screens: currentScreens,
    actions: currentActions,
  }));
};
